import { apiSuccessfulCode } from '../constants';
import _ from 'lodash';

/**
 * Parse response body as JSON when present; empty bodies resolve to null
 * instead of throwing "Unexpected end of JSON input".
 * A failed response whose body isn't JSON resolves to its trimmed text, so a plain-text
 * reason ("You don't have permission to do that") reaches getApiErrorMessage.
 * Successful non-JSON bodies still resolve to null.
 */
const parseResponseJson = (response) =>
  response.text().then((text) => {
    if (!text || !text.trim()) {
      return null;
    }
    try {
      return JSON.parse(text);
    } catch {
      return response.ok === false ? text.trim() : null;
    }
  });

/**
 * Fetch data from given url
 * @param {*} url
 * @param {*} options
 */
export const fetchJSON = (url, options = {}) => {
  return fetch(url, options)
    .then((response) => ({
      status: response.status,
      data: parseResponseJson(response),
    }))
    .catch((error) => {
      throw error;
    });
};

export const fetchJSONAsync = async (url, options = {}) => {
  try {
    const response = await fetch(url, options);
    const data = await parseResponseJson(response);

    return {
      status: response.status,
      data,
    };
  } catch (error) {
    throw error;
  }
};

export const fetchJSONWithoutData = (url, options = {}) => {
  return fetch(url, options)
    .then((response) => ({ status: response.status }))
    .catch((error) => {
      throw error;
    });
};

export const fetchBasicPostMethod = (path, username, password, body) => {
  var promise = fetch(path, {
    method: 'POST',
    headers: {
      Authorization: 'Basic ' + btoa(`${username}:${password}`),
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  return getResponseObject(promise);
};

export const fetchBasicPostMethodWithToken = (path, token, body) => {
  const fetchOptions = {
    method: 'POST',
    headers: {
      Authorization: 'Bearer ' + token,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  };
  return fetchJSON(path, fetchOptions);
  // var promise = fetch(path, {
  //     method: 'POST',
  //     headers: {
  //         'Authorization': 'Bearer ' + token,
  //         'Accept': 'application/json',
  //         'Content-Type': 'application/json'
  //     },
  //     body: JSON.stringify(body)
  // });
  // return getResponseObject(promise);
  // return fetchJSON(path, fetchOptions)
  // var promise = fetch(path, {
  //     method: 'POST',
  //     headers: {
  //         'Authorization': 'Bearer ' + token,
  //         'Accept': 'application/json',
  //         'Content-Type': 'application/json'
  //     },
  //     body: JSON.stringify(body)
  // });
  // return getResponseObject(promise);
};

export const fetchPostMethod = (path, userToken, body, withoutResponseBody = false) => {
  const data = new FormData();
  _.forEach(body, (item, key) => {
    if (Array.isArray(item)) {
      _.forEach(item, (itemArray, index) => {
        data.append(`${key}[${index}]`, itemArray);
      });
    } else {
      data.append(key, item);
    }
  });
  const fetchOptions = {
    method: 'POST',
    headers: {
      Authorization: 'Bearer ' + userToken,
    },
    body: data,
  };
  if (withoutResponseBody) {
    return fetchJSONWithoutData(path, fetchOptions);
  }
  return fetchJSON(path, fetchOptions);
};

export const fetchPostMethodWithFormData = (path, userToken, body) => {
  return fetchJSON(path, {
    method: 'POST',
    headers: {
      Authorization: 'Bearer ' + userToken,
    },
    body: body,
  });
};

export const fetchGetMethodAsync = async (path, userToken) => {
  const headers = {
    Accept: 'application/json',
  };
  if (userToken) {
    headers.Authorization = 'Bearer ' + userToken;
  }
  return fetchJSONAsync(path, {
    method: 'GET',
    headers,
  });
};

export const fetchGetMethod = (path, userToken) => {
  return fetchJSON(path, {
    method: 'GET',
    headers: {
      Authorization: 'Bearer ' + userToken,
      Accept: 'application/json',
    },
  });
};

export const fetchGetDataObjectMethod = (path, userToken) => {
  return fetch(path, {
    method: 'GET',
    headers: {
      Authorization: 'Bearer ' + userToken,
      Accept: 'application/octet-stream',
      'Content-Type': 'application/json',
    },
  });
};

export const downloadDataFromApiPromise = (promise, defaultErrorMessage, fileName) => {
  return new Promise((accept) => {
    promise
      .blob()
      .then((blob) => {
        accept({ status: apiSuccessfulCode });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
      })
      .catch(() => {
        accept({ errorMessage: defaultErrorMessage });
      });
  });
};

export const GetErrorMessageFromStatus = (response) => {
  switch (response.status) {
    case 500:
      return 'Internal Server Error';
    case 401:
      return "You don't have permission to access the system.";
    case 404:
      return 'Not found.';
    case 400:
      return 'Bad request';
    default:
      return;
  }
};

/** The readable message an API error response carries, if any. */
export const getServerMessage = (response) => {
  const body = response?.body ?? response?.data;
  // fetchJSON keeps a failed response's non-JSON text; an HTML error page is not a message.
  if (typeof body === 'string' && body.trim() && !body.trim().startsWith('<')) {
    return body.trim();
  }
  if (typeof body?.message === 'string' && body.message.trim()) {
    return body.message.trim();
  }
  if (typeof body?.Message === 'string' && body.Message.trim()) {
    return body.Message.trim();
  }
  return undefined;
};

/**
 * A failed `fetchJSON` response as `{ status, body }`, with its body read, so
 * callers can show the server's message (see `getServerMessage`).
 */
export const readFailedResponse = (response) =>
  Promise.resolve(response?.data).then(
    (body) => ({ status: response.status, body: body ?? null }),
    () => ({ status: response.status, body: null })
  );

/** Prefer an API-provided message over the generic status fallback. */
export const GetErrorMessage = (response) =>
  getServerMessage(response) ?? GetErrorMessageFromStatus(response);

export const SOMETHING_WENT_WRONG_MESSAGE = 'Something went wrong. Please try again.';

/**
 * The text for a failed request (`{ status, body }`), never empty: the
 * server's message, else the status text, else a generic error.
 */
export const failureMessage = (failure) => GetErrorMessage(failure ?? {}) ?? SOMETHING_WENT_WRONG_MESSAGE;

function getResponseObject(promise) {
  return new Promise((accept) => {
    promise
      .then((response) => {
        response
          .text()
          .then((text) => {
            let body = null;
            if (text && text.trim()) {
              try {
                body = JSON.parse(text);
              } catch {
                // ASP.NET sometimes returns a plain string body on BadRequest
                body = text.trim();
              }
            }
            accept({ body, status: response.status });
          })
          .catch((error) => {
            console.log('getResponseObject - error (json response catch)', error);
            accept({ status: response.status, keyErrorMessage: error.message });
          });
      })
      .catch((error) => {
        console.log('qual o erro', error, promise);
        accept({ status: 500, serviceUnavailable: true });
      });
  });
}
